import React from 'react';

interface PasswordValidationResult {
  isValid: boolean;
  errors: string[];
}

export const validatePassword = (password: string): PasswordValidationResult => {
  const errors: string[] = [];
  
  if (password.length < 12) {
    errors.push('Password must be at least 12 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character (!@#$%^&*(),.?":{}|<>)');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

interface PasswordRequirementsProps {
  className?: string;
  password?: string;
}

export const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({ className, password = '' }) => {
  const hasMinLength = password.length >= 12;
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  
  const getItemStyle = (isValid: boolean) => {
    if (password.length === 0) return ''; // No styling if password field is empty
    return isValid ? 'text-green-600' : 'text-red-600';
  };

  const getIcon = (isValid: boolean) => {
    if (password.length === 0) return '•'; // Default bullet point if password field is empty
    return isValid ? '✓' : '✗';
  };

  return (
    <div className={`mt-1 text-sm ${className || ''}`}>
      <p className="font-medium mb-1">Password must meet the following criteria:</p>
      <ul className="pl-5 space-y-1">
        <li className={getItemStyle(hasMinLength)}>
          <span className="mr-1">{getIcon(hasMinLength)}</span> At least 12 characters long
        </li>
        <li className={getItemStyle(hasUppercase)}>
          <span className="mr-1">{getIcon(hasUppercase)}</span> At least one uppercase letter
        </li>
        <li className={getItemStyle(hasLowercase)}>
          <span className="mr-1">{getIcon(hasLowercase)}</span> At least one lowercase letter
        </li>
        <li className={getItemStyle(hasNumber)}>
          <span className="mr-1">{getIcon(hasNumber)}</span> At least one number
        </li>
        <li className={getItemStyle(hasSpecial)}>
          <span className="mr-1">{getIcon(hasSpecial)}</span> At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)
        </li>
      </ul>
    </div>
  );
};
